import { Link } from 'react-router';
import type { DeployPreviewDTO } from '@devrobot/shared';
import { ArrowRight, CircleAlert, CircleCheck, TriangleAlert } from 'lucide-react';
import { RelativeTime } from '../../../components/RelativeTime';
import { StateDimensions } from '../../../components/StateDimensions';
import { StatusBadge } from '../../../components/StatusBadge';
import { shortSha } from '../../../lib/format';

function Check({ ok, warn, children }: { ok: boolean; warn?: boolean; children: React.ReactNode }) {
  const Icon = ok ? CircleCheck : warn ? TriangleAlert : CircleAlert;
  const cls = ok ? 'dr-ink-success' : warn ? 'dr-ink-warning' : 'dr-ink-error';
  return (
    <li className="flex items-start gap-2 text-sm">
      <Icon className={`mt-0.5 size-4 shrink-0 ${cls}`} aria-hidden="true" />
      <div className="min-w-0 flex-1">{children}</div>
    </li>
  );
}

/** What the deployment would do, and whether anything blocks it (server-side validation). */
export function DeployPreviewPanel({ p, stack, configHref }: { p: DeployPreviewDTO; stack: string; configHref: string }) {
  const ex = p.existing;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-[1fr_auto_1fr]">
        <div className="rounded-field border border-transparent bg-base-200/60 p-3">
          <p className="text-xs font-medium tracking-wide text-base-content/55 uppercase">Currently on {p.vps.name}</p>
          {ex ? (
            <div className="mt-1 space-y-2">
              {ex.release ? (
                <p className="text-sm">
                  <span className="font-mono">
                    {ex.release.branch}@{ex.release.shortSha}
                  </span>
                  {ex.release.commitTitle ? <span className="block text-base-content/65">{ex.release.commitTitle}</span> : null}
                </p>
              ) : (
                <p className="text-sm text-base-content/65">A target exists but no release is recorded.</p>
              )}
              <StateDimensions state={ex.state} compact />
              <Link className="link text-xs" to={`/targets/${ex.targetId}`}>
                Open target
              </Link>
            </div>
          ) : (
            <p className="mt-1 text-sm text-base-content/65">
              Not deployed here yet. A new target <span className="font-mono">{stack}</span> will be created.
            </p>
          )}
        </div>
        <div className="hidden items-center md:flex" aria-hidden="true">
          <span className="grid size-8 place-items-center rounded-full border border-hairline bg-base-100 dr-shadow-xs">
            <ArrowRight className="size-4 text-base-content/50" />
          </span>
        </div>
        <div className={`rounded-field border p-3 transition-colors duration-300 ${p.replacesDifferentRelease ? 'border-warning/50 bg-warning/10' : 'border-transparent bg-base-200/60'}`}>
          <p className="text-xs font-medium tracking-wide text-base-content/55 uppercase">Requested</p>
          {p.branch ? (
            <div className="mt-1 space-y-1 text-sm">
              <p className="font-mono">
                {p.branch.name}@{shortSha(p.branch.commitSha)}
              </p>
              <p className="text-base-content/65">{p.branch.commitTitle}</p>
              <p className="text-xs text-base-content/60">
                committed <RelativeTime value={p.branch.committedAt} />
              </p>
            </div>
          ) : (
            <p className="mt-1 text-sm dr-ink-error">This branch does not exist in GitLab.</p>
          )}
        </div>
      </div>

      <ul className="dr-stagger space-y-2.5">
        <Check ok={p.branchRule.allowed}>
          {p.branchRule.allowed ? 'Branch allowed' : 'Branch not allowed'} in {p.environment.name}: <span className="text-base-content/65">{p.branchRule.rule}</span>
          {p.branchRule.reason ? <span className="block dr-ink-error">{p.branchRule.reason}</span> : null}
        </Check>
        <Check ok={p.vps.ready && p.vps.warnings.length === 0} warn={p.vps.ready}>
          VPS {p.vps.name}: <StatusBadge kind="connection" value={p.vps.connection} size="xs" /> {p.vps.ready ? 'ready for deployments' : 'not ready for deployments'}
          {p.vps.warnings.map((w, i) => (
            <span key={i} className="block text-base-content/80">
              <span className="badge badge-soft badge-warning badge-xs mr-1">warning</span>
              {w}
            </span>
          ))}
        </Check>
        <Check ok={p.config.complete}>
          {p.config.complete ? (
            'All required configuration and files are set'
          ) : (
            <>
              Configuration of {p.environment.name} is incomplete.{' '}
              <Link className="link" to={configHref}>
                Set the missing values
              </Link>
              <ul className="mt-1 flex flex-wrap gap-1">
                {p.config.missing.map((m) => (
                  <li key={m} className="badge badge-error badge-soft badge-sm font-mono">
                    {m}
                  </li>
                ))}
                {p.config.missingFiles.map((m) => (
                  <li key={m} className="badge badge-error badge-soft badge-sm font-mono">
                    file: {m}
                  </li>
                ))}
              </ul>
            </>
          )}
        </Check>
        {p.collisions.length > 0 ? (
          <Check ok={false}>
            Resource collisions
            <ul className="list-disc pl-5">
              {p.collisions.map((c, i) => (
                <li key={i} className="break-anywhere">
                  {c}
                </li>
              ))}
            </ul>
          </Check>
        ) : null}
        {p.blockers.length > 0 ? (
          <Check ok={false}>
            Blocked
            <ul className="list-disc pl-5">
              {p.blockers.map((c, i) => (
                <li key={i} className="break-anywhere">
                  {c}
                </li>
              ))}
            </ul>
          </Check>
        ) : null}
        {p.warnings.length > 0 ? (
          <Check ok={false} warn>
            Warnings
            <ul className="list-disc pl-5">
              {p.warnings.map((c, i) => (
                <li key={i} className="break-anywhere">
                  {c}
                </li>
              ))}
            </ul>
          </Check>
        ) : null}
      </ul>
    </div>
  );
}
